import React, { useRef, useEffect, useState } from "react";

interface Props {
  logoUrl: string;
  position: { x: number; y: number };                   
  setPosition: (pos: { x: number; y: number }) => void; 
  size: number;                                         
}

export default function DraggableLogo({
  logoUrl,
  position,
  setPosition,
  size,
}: Props) {
  const logoRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLElement | null>(null);
  const [contentBox, setContentBox] = useState<{
    width: number;
    height: number;
    offsetX: number;
    offsetY: number;
  } | null>(null);

  const [dragging, setDragging] = useState(false);
  const [startPos, setStartPos] = useState({ x: 0, y: 0 });
  useEffect(() => {
    if (logoRef.current) {
      containerRef.current = logoRef.current.closest(
        ".video-container"
      ) as HTMLElement;
      updateContentBox();
    }
    window.addEventListener("resize", updateContentBox);
    return () => window.removeEventListener("resize", updateContentBox);
  }, [logoRef.current]);

  function updateContentBox() {
    if (!containerRef.current) return;
    const videoEl = containerRef.current.querySelector(
      "video"
    ) as HTMLVideoElement;
    if (!videoEl || !videoEl.videoWidth) return;

    const contRect = containerRef.current.getBoundingClientRect();
    const vidRect = videoEl.getBoundingClientRect();

    const videoAR = videoEl.videoWidth / videoEl.videoHeight;
    const contAR = vidRect.width / vidRect.height;

    let contentW: number, contentH: number, offsetX: number, offsetY: number;

    if (contAR > videoAR) {
      // on a des barres à gauche / droite
      contentH = vidRect.height;
      contentW = contentH * videoAR;
      offsetX = vidRect.left + (vidRect.width - contentW) / 2 - contRect.left;
      offsetY = vidRect.top - contRect.top;
    } else {
      // barres en haut / bas (rare)
      contentW = vidRect.width;
      contentH = contentW / videoAR;
      offsetX = vidRect.left - contRect.left;
      offsetY =
        vidRect.top + (vidRect.height - contentH) / 2 - contRect.top;
    }

    setContentBox({ width: contentW, height: contentH, offsetX, offsetY });
  }

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    updateContentBox();
    setDragging(true);
    setStartPos({ x: e.clientX, y: e.clientY });
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (!dragging || !logoRef.current || !contentBox) return;

    const dx = e.clientX - startPos.x;
    const dy = e.clientY - startPos.y;

    // position courante en px dans la content box
    const curX = (position.x / 100) * contentBox.width;
    const curY = (position.y / 100) * contentBox.height;

    let newX = curX + dx;
    let newY = curY + dy;

    // on clamp pour rester dans la content box
    const halfW = size / 2;
    const halfH = size / 2;
    newX = Math.max(halfW, Math.min(contentBox.width - halfW, newX));
    newY = Math.max(halfH, Math.min(contentBox.height - halfH, newY));

    // on reconvertit en %
    const newXpct = (newX / contentBox.width) * 100;
    const newYpct = (newY / contentBox.height) * 100;

    setPosition({ x: newXpct, y: newYpct });
    setStartPos({ x: e.clientX, y: e.clientY });
  };

  const handleMouseUp = () => setDragging(false);

  useEffect(() => {
    if (dragging) {
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
    } else {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    }
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [dragging, contentBox, startPos, position]);

  // calcul du style final en pixel (compense les bandes)
  let style: React.CSSProperties = {};
  if (contentBox) {
    const leftPx =
      contentBox.offsetX + (position.x / 100) * contentBox.width;
    const topPx =
      contentBox.offsetY + (position.y / 100) * contentBox.height;
    style = {
      position: "absolute",
      left: `${leftPx}px`,
      top: `${topPx}px`,
      width: `${size}px`,
      height: `${size}px`,
      transform: "translate(-50%, -50%)",
      cursor: "move",
      zIndex: 10,
    };
  }

  return (
    <div ref={logoRef} onMouseDown={handleMouseDown} style={style} className="border border-gray-400">
      <img
        src={logoUrl}
        alt="Logo"
        className="w-full h-full object-contain rounded"
      />
    </div>
  );
}